import {
  XAxis,
  YAxis,
  CartesianGrid,
  AreaChart,
  Tooltip,
  Area,
  ResponsiveContainer,
} from "recharts";

import { FC, useContext } from "react";
import { UseQueryResult } from "@tanstack/react-query";
import { IDetailsTeamResponse } from "../../../../models/endpoints/Team";
import dayjs from "dayjs";
import style from "./Charts.module.scss";
import Typhography from "../../../../shared/Typhography/Typhography";
import { InfoIcon } from "@phosphor-icons/react";
import cx from "classnames";
import { IconButton, Skeleton } from "@chakra-ui/react";
import { InfoDrawerContext } from "../../../../providers/InfoDrawer";

/** Colori del grafico: dai token del tema, con fallback per sicurezza. */
const LINEA = "var(--c-primary, #0ea5e9)";
const GRIGLIA = "var(--c-border-0, #e7e7e7)";

export const StressChart: FC<{
  query: UseQueryResult<IDetailsTeamResponse, Error>;
}> = ({ query }) => {
  /**
   * Serie giornaliera dello stress. I giorni senza compilazioni valgono `null`
   * e vanno saltati, non disegnati a zero.
   */
  const serie = query.data?.averageStressByDays ?? [];
  const haCompilazioni = serie.some((punto) => typeof punto.mean === "number");
  const { setState } = useContext(InfoDrawerContext);
  return (
    <Skeleton loading={query.isPending}>
      <div className={style.container}>
        <div className="d-flex justify-content-between align-items-center w-100 mb-4">
          <div className="d-flex align-items-center gap-1">
            <Typhography size="bold-14">Analisi dello stress</Typhography>
            {typeof query.data?.coverage?.stress === "number" &&
              query.data.coverage.stress > 0 && (
                <Typhography size="regular-12" className={style.coverage}>
                  {query.data.coverage.stress === query.data.coverage.athletes
                    ? `${query.data.coverage.athletes} atleti`
                    : `${query.data.coverage.stress} di ${query.data.coverage.athletes} atleti`}
                </Typhography>
              )}
            <InfoIcon
              className={style.infoIcon}
              onClick={() => {
                setState("sleep");
              }}
            />
          </div>
          <div className="d-flex align-items-center gap-1">
            <Typhography size="bold-20">
              {query.data?.stressAverage
                ? query.data.stressAverage.toFixed(1)
                : "-"}
            </Typhography>

            {typeof query.data?.stressAverage === "number" && (
              // Stress alto = brutta notizia. I due stati erano invertiti:
              // uno stress medio di 4/5 veniva colorato di verde.
              <div
                className={cx(style.percentageContainer, {
                  [style.good]: query.data.stressAverage < 2,
                  [style.bad]: query.data.stressAverage > 3,
                  [style.mid]:
                    query.data.stressAverage >= 2 &&
                    query.data.stressAverage <= 3,
                })}
              >
                <Typhography size="bold-12">
                  {query.data.stressAverage < 2
                    ? "Basso"
                    : query.data.stressAverage < 3
                    ? "Medio"
                    : "Alto"}
                </Typhography>
              </div>
            )}
          </div>
        </div>
        {!haCompilazioni && !query.isPending && (
          <Typhography size="regular-12" className={style.emptyState}>
            Nessun questionario compilato negli ultimi 14 giorni.
          </Typhography>
        )}

        {/* Senza nessuna compilazione il grafico non si disegna affatto: gli
            assi di una serie vuota occupano mezzo schermo e si leggono come un
            grafico rotto. Resta solo la riga che spiega perché è vuoto.

            ResponsiveContainer diretto, come ogni altro grafico dell'app:
            `Chart.Root` di Chakra rendeva il wrapper dimensionato ma senza
            nessun <svg> dentro, quindi il grafico non si è mai visto. */}
        <div className={style.chartArea} hidden={!haCompilazioni}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={serie}
              margin={{ top: 8, right: 12, left: -12, bottom: 4 }}
            >
              <CartesianGrid stroke={GRIGLIA} vertical={false} />
              <XAxis
                axisLine={false}
                dataKey="date"
                tickFormatter={(value) => {
                  const date = dayjs(value);
                  const today = dayjs();
                  const yesterday = dayjs().subtract(1, "day");

                  if (date.isSame(today, "day")) {
                    return "Oggi";
                  }

                  if (date.isSame(yesterday, "day")) {
                    return "Ieri";
                  }

                  return date.format("DD/MM");
                }}
                stroke={GRIGLIA}
                tick={{ fontSize: 11 }}
              />
              {/* Scala fissa del questionario (1-5): con dominio automatico due
                  giorni compilati bastavano a far sembrare enorme una
                  differenza di un punto. */}
              <YAxis
                axisLine={false}
                tickLine={false}
                tickMargin={8}
                domain={[1, 5]}
                ticks={[1, 2, 3, 4, 5]}
                stroke={GRIGLIA}
                tick={{ fontSize: 11 }}
              />
              <Tooltip
                animationDuration={100}
                cursor={false}
                formatter={(value: number) => [value.toFixed(1), "stress"]}
                labelFormatter={(value) => dayjs(value).format("DD/MM/YYYY")}
              />
              {/**
               * NIENTE `connectNulls`: univa il 25/07 al 05/08 con una retta,
               * disegnando una salita continua attraverso undici giorni in cui
               * nessuno ha compilato. Tre misure non sono una tendenza.
               *
               * Senza, la linea collega solo giorni consecutivi davvero
               * compilati, e il pallino tiene visibili le misure isolate.
               */}
              <Area
                type="monotone"
                dataKey="mean"
                isAnimationActive={false}
                fill={LINEA}
                fillOpacity={0.18}
                stroke={LINEA}
                strokeWidth={2}
                dot={{ r: 3, fill: LINEA }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </Skeleton>
  );
};
